"use client";

import { useMemo } from "react";
import { useTranslations } from "next-intl";

interface AssessmentBadgeProps {
  score: number | null;
  delta: number | null;
  confidence: number | null;
  updatedAt: string | null;
}

export default function AssessmentBadge({
  score,
  delta,
  confidence,
  updatedAt,
}: AssessmentBadgeProps) {
  const t = useTranslations("chat");

  const deltaLabel = useMemo(() => {
    if (typeof delta !== "number") return null;
    if (delta === 0) return "±0";
    return delta > 0 ? `+${delta.toFixed(1)}` : delta.toFixed(1);
  }, [delta]);

  const confidenceLabel = useMemo(() => {
    if (typeof confidence !== "number" || !Number.isFinite(confidence))
      return null;
    return `${Math.round(confidence * 100)}%`;
  }, [confidence]);

  const updatedLabel = useMemo(() => {
    if (!updatedAt) return null;
    try {
      const date = new Date(updatedAt);
      if (Number.isNaN(date.getTime())) return null;
      return date.toLocaleString();
    } catch {
      return null;
    }
  }, [updatedAt]);

  if (score == null && deltaLabel == null && confidenceLabel == null) {
    return null;
  }

  return (
    <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200/70 bg-emerald-50/70 px-3 py-1 text-xs text-emerald-900 shadow-sm dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-100">
      <span className="font-semibold">
        {t("assessment.badge_title", { default: "最近评估" })}
      </span>
      {score != null ? (
        <span>
          {t("assessment.badge_score", { default: "得分" })}:{" "}
          <strong>{score.toFixed(1)}</strong>
        </span>
      ) : null}
      {deltaLabel ? (
        <span>
          {t("assessment.badge_delta", { default: "变化" })}:{" "}
          <strong>{deltaLabel}</strong>
        </span>
      ) : null}
      {confidenceLabel ? (
        <span>
          {t("assessment.badge_confidence", { default: "置信" })}:{" "}
          {confidenceLabel}
        </span>
      ) : null}
      {updatedLabel ? (
        <span className="text-[11px] text-muted-foreground">
          {t("assessment.badge_updated", { default: "更新于" })} {updatedLabel}
        </span>
      ) : null}
    </div>
  );
}
